/* Barney Paw — behaviour for the custom bp-* sections */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

// Customer-facing strings come from the locale files (rendered in layout/theme.liquid)
const S = Object.assign({ add: '+ Add', soldOut: 'Sold out', unavailable: 'Unavailable', inStock: 'In stock', added: 'Added ✓', calculating: 'Calculating…', noRates: 'No shipping options for this address yet.', linkCopied: 'Cart link copied!', shareFailed: 'Could not share the cart' }, window.bpStrings || {});

/* ---------- Countdown ---------- */
class BpCountdown extends HTMLElement {
  connectedCallback() {
    const raw = (this.dataset.end || '').trim();
    this.daily = raw.toLowerCase() === 'daily';
    this.end = this.daily ? this.nextMidnight() : new Date(raw.replace(' ', 'T')).getTime();
    if (!raw || Number.isNaN(this.end)) return;
    if (this.daily) this.classList.add('bp-countdown--daily');
    this.units = {};
    $$('[data-unit]', this).forEach((el) => (this.units[el.dataset.unit] = el));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
  tick() {
    if (this.daily && this.end - Date.now() <= 0) this.end = this.nextMidnight();
    const left = this.end - Date.now();
    if (left <= 0) {
      this.hidden = true;
      clearInterval(this.timer);
      return;
    }
    this.hidden = false;
    const s = Math.floor(left / 1000);
    this.units.d.textContent = Math.floor(s / 86400);
    this.units.h.textContent = String(Math.floor((s % 86400) / 3600)).padStart(2, "0");
    this.units.m.textContent = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    this.units.s.textContent = String(s % 60).padStart(2, "0");
  }
}
BpCountdown.prototype.nextMidnight = function () {
  // midnight in the store's time zone (data-offset like "-0400"), when the daily deal switches
  const m = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.offset || '');
  if (m) {
    const offset = (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) * 60000;
    const storeNow = new Date(Date.now() + offset);
    return Date.UTC(storeNow.getUTCFullYear(), storeNow.getUTCMonth(), storeNow.getUTCDate() + 1) - offset;
  }
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.getTime();
};
customElements.get('bp-countdown') || customElements.define('bp-countdown', BpCountdown);

/* ---------- Announcement bar ---------- */
class BpAnnounce extends HTMLElement {
  connectedCallback() {
    this.slides = $$('.bp-announce__slide', this);
    this.index = 0;
    const bar = this.closest('.bp-announce');
    $('[data-bp-announce-prev]', bar)?.addEventListener('click', () => this.go(this.index - 1, true));
    $('[data-bp-announce-next]', bar)?.addEventListener('click', () => this.go(this.index + 1, true));
    this.start();
  }
  start() {
    clearInterval(this.timer);
    if (this.slides.length < 2) return;
    this.timer = setInterval(() => this.go(this.index + 1), (Number(this.dataset.speed) || 6) * 1000);
  }
  go(i, user) {
    if (!this.slides.length) return;
    this.slides[this.index].classList.remove('is-active');
    this.index = (i + this.slides.length) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
    if (user) this.start();
  }
}
customElements.get('bp-announce') || customElements.define('bp-announce', BpAnnounce);

/* ---------- Slideshow ---------- */
class BpSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = $$('.bp-slide', this);
    this.thumbs = $$('.bp-slideshow__thumb', this);
    this.index = 0;
    $('[data-bp-slide-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
    $('[data-bp-slide-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.bpSlideGo), true)));
    this.start();
  }
  start() {
    clearInterval(this.timer);
    const secs = Number(this.dataset.autoplay);
    if (secs > 0 && this.slides.length > 1) this.timer = setInterval(() => this.go(this.index + 1), secs * 1000);
  }
  go(i, user) {
    const n = this.slides.length;
    if (n < 2) return;
    this.slides[this.index].classList.remove('is-active');
    this.index = (i + n) % n;
    this.slides[this.index].classList.add('is-active');
    this.thumbs.forEach((t, k) => t.classList.toggle('is-next', k === (this.index + 1) % n));
    if (user) this.start();
  }
}
customElements.get('bp-slideshow') || customElements.define('bp-slideshow', BpSlideshow);

/* ---------- Tabs ---------- */
class BpTabs extends HTMLElement {
  connectedCallback() {
    // Detail tabs: build the tab buttons from the panels that have content
    const list = $('[data-bp-dtabs-list]', this);
    if (list && !list.children.length) {
      $$('[data-bp-panel]', this).forEach((panel, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bp-tabs__tab';
        btn.setAttribute('role', 'tab');
        btn.dataset.bpTab = panel.dataset.bpPanel;
        btn.setAttribute('aria-selected', String(i === 0));
        btn.textContent = panel.dataset.title;
        list.appendChild(btn);
      });
      if (list.children.length < 2) list.hidden = true;
    }
    const tabs = $$('[data-bp-tab]', this);
    const panels = $$('[data-bp-panel]', this);
    tabs.forEach((tab) =>
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        panels.forEach((p) => (p.hidden = p.dataset.bpPanel !== tab.dataset.bpTab));
      })
    );
  }
}
customElements.get('bp-tabs') || customElements.define('bp-tabs', BpTabs);

/* ---------- Featured product: gallery + variants ---------- */
class BpFeaturedProduct extends HTMLElement {
  connectedCallback() {
    this.main = $('[data-bp-main-image]', this);
    $$('[data-bp-thumb]', this).forEach((thumb) =>
      thumb.addEventListener('click', () => {
        $$('[data-bp-thumb]', this).forEach((t) => t.classList.toggle('is-active', t === thumb));
        this.showImage(thumb.dataset.bpThumb);
      })
    );
    const json = $('[data-bp-product-json]', this);
    if (!json) return;
    this.variants = JSON.parse(json.textContent).variants;
    this.fieldsets = $$('[data-bp-option]', this);
    this.fieldsets.forEach((fs) =>
      fs.addEventListener('click', (e) => {
        const btn = e.target.closest('.bp-options__btn');
        if (!btn) return;
        $$('.bp-options__btn', fs).forEach((b) => b.classList.toggle('is-selected', b === btn));
        $('[data-bp-option-value]', fs).textContent = btn.dataset.label || btn.dataset.value;
        this.update();
      })
    );
  }
  showImage(src) {
    if (!this.main || !src) return;
    this.main.removeAttribute('srcset');
    this.main.src = src;
  }
  update() {
    const selected = this.fieldsets.map((fs) => $('.bp-options__btn.is-selected', fs)?.dataset.value);
    markSoldOut(this.fieldsets, this.variants, selected);
    const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    const button = $('[data-bp-add-button]', this);
    if (!variant) {
      button.disabled = true;
      button.textContent = S.unavailable;
      return;
    }
    $('[data-bp-variant-id]', this).value = variant.id;
    const sku = $('[data-bp-sku]', this);
    if (sku) {
      sku.textContent = variant.sku || '';
      sku.parentElement.hidden = !variant.sku;
    }
    const price = $('[data-bp-price]', this);
    price.classList.toggle('bp-price--sale', Boolean(variant.compare));
    price.innerHTML =
      `<span class="bp-price__current">${variant.price}</span>` +
      (variant.compare ? `<s class="bp-price__compare">${variant.compare}</s><span class="bp-price__off">-${variant.off}%</span>` : '');
    button.disabled = !variant.available;
    button.textContent = variant.available ? S.add : S.soldOut;
    if (variant.image) this.showImage(variant.image);
  }
}
customElements.get('bp-featured-product') || customElements.define('bp-featured-product', BpFeaturedProduct);

/* ---------- Option buttons: red "Sold out" band on values with no stock for the current choice ---------- */
function markSoldOut(fieldsets, variants, selected) {
  fieldsets.forEach((fs, i) => {
    $$('.bp-options__btn', fs).forEach((btn) => {
      const inStock = variants.some((v) => v.available && v.options[i] === btn.dataset.value && v.options.every((o, j) => j === i || !selected[j] || o === selected[j]));
      btn.classList.toggle('is-soldout', !inStock);
    });
  });
}

/* ---------- Toast ---------- */
function toast(message) {
  let el = $('.bp-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'bp-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('is-visible'), 2500);
}

/* ---------- Personalization in the order note ----------
   Line item properties (e.g. "Pet name") are also written at the end of the order note, after a marker,
   so the supplier app, which passes the order note on as the order remark, receives them.
   The customer's own note stays above the marker. */
const NOTE_MARK = '[Personalization]';
const customerNote = (note) => String(note || '').split(NOTE_MARK)[0].trim();
function personalizationText(cart) {
  const lines = [];
  (cart.items || []).forEach((item) => {
    Object.entries(item.properties || {}).forEach(([key, value]) => {
      if (!value || key.startsWith('_')) return;
      const variant = item.variant_title ? ` (${item.variant_title})` : '';
      lines.push(`${key}: ${value} | ${item.product_title}${variant} x${item.quantity}`);
    });
  });
  return lines.length ? `${NOTE_MARK}\n${lines.join('\n')}` : '';
}
async function syncPersonalizationNote(cart, userNote) {
  if (!cart) return;
  const base = userNote !== undefined ? String(userNote).trim() : customerNote(cart.note);
  const note = [base, personalizationText(cart)].filter(Boolean).join('\n\n');
  if (note === (cart.note || '')) return;
  await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
}
// the customer edits only their part of the note; the personalization block is kept
async function saveCustomerNote(value) {
  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
  await syncPersonalizationNote(cart, value);
}

/* ---------- Cart ---------- */
async function refreshCartCount() {
  try {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
    $$('[data-bp-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    syncPersonalizationNote(cart).catch(() => {});
    return cart;
  } catch (e) {
    return null;
  }
}

async function addToCart(form) {
  const button = $('button[type="submit"]', form);
  button?.classList.add('is-loading');
  const formData = new FormData(form);
  const sectionIds = $$('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((r) => r.json());
    if (response.status) throw new Error(response.description || response.message || 'Could not add to cart');
    const cart = await refreshCartCount();
    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
      detail: { items: cart?.items, source: 'bp-product-card', itemCount: quantity, sections: response.sections, didError: false },
    });
  } catch (error) {
    deferred.resolve({ detail: { didError: true } });
    toast(error.message);
  } finally {
    button?.classList.remove('is-loading');
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-bp-add]');
  if (!form) return;
  e.preventDefault();
  addToCart(form);
});

document.addEventListener(StandardEvents?.cartLinesUpdate || 'cart:lines-update', (e) => {
  e.promise?.then(() => refreshCartCount()).catch(() => {});
});

/* ---------- Clicks ---------- */
document.addEventListener('click', (e) => {
  const qtyBtn = e.target.closest('[data-bp-qty]');
  if (qtyBtn) {
    const input = $('.bp-qty__input', qtyBtn.closest('.bp-qty'));
    input.value = Math.max(1, (Number(input.value) || 1) + Number(qtyBtn.dataset.bpQty));
    return;
  }

  if (e.target.closest('[data-bp-drawer-close]')) {
    e.target.closest('[data-bp-drawer]')?.removeAttribute('open');
    return;
  }

  if (e.target.closest('[data-bp-to-top]')) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  const videoToggle = e.target.closest('[data-bp-video-toggle]');
  if (videoToggle) {
    const video = $('video', videoToggle.parentElement);
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
    videoToggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    return;
  }

  const ics = e.target.closest('[data-bp-ics]');
  if (ics) {
    e.preventDefault();
    const d = ics.dataset;
    const body = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Ruffbay//Events//EN', 'BEGIN:VEVENT',
      `UID:${d.start}-${Math.random().toString(36).slice(2)}@ruffbay`,
      `DTSTART:${d.start}`, `DTEND:${d.end}`, `SUMMARY:${d.title}`, `LOCATION:${d.location}`, `DESCRIPTION:${d.details}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([body], { type: 'text/calendar' }));
    a.download = `${d.title || 'event'}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
});

/* ---------- Header search: category + voice ---------- */
$$('[data-bp-search]').forEach((form) => {
  form.addEventListener('submit', (e) => {
    const category = $('[data-bp-search-category]', form)?.value;
    const q = $('input[name="q"]', form).value.trim();
    if (category && !q) {
      e.preventDefault();
      window.location.href = category;
    }
  });
  const voice = $('[data-bp-voice]', form);
  const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (voice && Recognition) {
    voice.hidden = false;
    voice.addEventListener('click', () => {
      const rec = new Recognition();
      rec.lang = document.documentElement.lang || 'en';
      rec.onresult = (ev) => {
        $('input[name="q"]', form).value = ev.results[0][0].transcript;
        form.requestSubmit();
      };
      rec.start();
    });
  }
});

/* ---------- Pet finder ---------- */
$$('[data-bp-finder]').forEach((form) => {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const val = (name) => $(`[data-finder="${name}"]`, form)?.value || '';
    const root = window.Shopify?.routes?.root || '/';
    const pet = val('pet');
    const params = new URLSearchParams();
    if (val('type')) params.append('filter.p.product_type', val('type'));
    if (val('size')) params.append('filter.v.option.size', val('size'));
    if (val('price')) {
      const [min, max] = val('price').split('-');
      if (min) params.append('filter.v.price.gte', min);
      if (max) params.append('filter.v.price.lte', max);
    }
    const query = params.toString();
    window.location.href = `${root}collections/${pet || 'all'}${query ? `?${query}` : ''}`;
  });
});

/* ---------- Back to top visibility ---------- */
const toTop = $('[data-bp-to-top]');
if (toTop) {
  const onScroll = () => toTop.classList.toggle('is-visible', window.scrollY > 600);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ---------- Scroll reveal ---------- */
const REVEAL_SELECTOR = [
  '.bp-section .bp-heading', '.bp-trust__item', '.bp-pets__col', '.bp-card', '.bp-cols__item', '.bp-finder__form',
  '.bp-img-card', '.bp-promo', '.bp-logo-row__item', '.bp-featured__gallery', '.bp-featured__panel', '.bp-banner__content', '.bp-breed', '.bp-size', '.bp-ptabs__promo',
  '.bp-stats > *', '.bp-itgrid__item', '.bp-testi__card', '.bp-glance > *', '.bp-event', '.bp-split > *', '.bp-post',
].join(',');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const revealObserver =
  !reducedMotion && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function setupReveal(root = document) {
  if (!revealObserver) return;
  document.documentElement.classList.add('bp-motion');
  $$(REVEAL_SELECTOR, root).forEach((el) => {
    if (el.hasAttribute('data-bp-reveal')) return;
    const siblings = Array.from(el.parentElement.children).filter((c) => c.matches(REVEAL_SELECTOR));
    const position = Math.max(0, siblings.indexOf(el));
    el.style.setProperty('--bp-delay', `${Math.min(position, 6) * 0.08}s`);
    el.setAttribute('data-bp-reveal', '');
    revealObserver.observe(el);
  });
}
setupReveal();
document.addEventListener('shopify:section:load', (e) => setupReveal(e.target));

/* ---------- Cart feedback ---------- */
document.addEventListener(StandardEvents?.cartLinesUpdate || 'cart:lines-update', (e) => {
  const form = e.target instanceof Element ? e.target.closest('[data-bp-add]') : null;
  e.promise
    ?.then(({ detail } = {}) => {
      if (detail?.didError) return;
      $$('[data-bp-cart-count]').forEach((el) => {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
      const button = form && $('button[type="submit"]', form);
      if (!button) return;
      const label = button.textContent;
      button.classList.add('is-added');
      button.textContent = S.added;
      setTimeout(() => {
        button.classList.remove('is-added');
        button.textContent = label;
      }, 1800);
    })
    .catch(() => {});
});

/* ---------- Drag-to-scroll rows (mouse) ---------- */
function setupDragScroll(root = document) {
  $$('.bp-banner__cards', root).forEach((row) => {
    if (row.dataset.bpDrag) return;
    row.dataset.bpDrag = '1';
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    row.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      startX = e.clientX;
      startScroll = row.scrollLeft;
      moved = false;
      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        if (Math.abs(dx) > 5) {
          moved = true;
          row.classList.add('is-dragging');
        }
        row.scrollLeft = startScroll - dx;
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        requestAnimationFrame(() => row.classList.remove('is-dragging'));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
    row.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        moved = false;
      }
    }, true);
    row.addEventListener('dragstart', (e) => e.preventDefault());
    setupAutoplay(row);
  });
}

/* Advances a scrollable row by one card every N seconds; loops; pauses on hover, touch, focus or when off-screen. */
function setupAutoplay(row) {
  const seconds = Number(row.dataset.autoplay);
  if (!seconds || reducedMotion) return;
  let paused = false;
  let visible = true;
  const step = () => {
    if (paused || !visible || row.classList.contains('is-dragging')) return;
    const max = row.scrollWidth - row.clientWidth;
    if (max <= 2) return;
    const card = row.firstElementChild;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const distance = card ? card.getBoundingClientRect().width + gap : row.clientWidth / 3;
    const target = row.scrollLeft >= max - 2 ? 0 : Math.min(row.scrollLeft + distance, max);
    row.scrollTo({ left: target, behavior: 'smooth' });
  };
  let timer = setInterval(step, seconds * 1000);
  const pause = () => (paused = true);
  const resume = () => (paused = false);
  row.addEventListener('mouseenter', pause);
  row.addEventListener('mouseleave', resume);
  row.addEventListener('focusin', pause);
  row.addEventListener('focusout', resume);
  row.addEventListener('touchstart', pause, { passive: true });
  row.addEventListener('touchend', () => setTimeout(resume, 4000), { passive: true });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => (visible = entry.isIntersecting)).observe(row);
  }
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target.contains(row)) clearInterval(timer);
  });
}
setupDragScroll();
document.addEventListener('shopify:section:load', (e) => setupDragScroll(e.target));

/* ---------- Mega menu: hovering a category shows its panel ---------- */
document.addEventListener('pointerover', (e) => {
  const cat = e.target.closest('[data-bp-mega-cat]');
  if (cat) activateMegaCat(cat);
});
document.addEventListener('focusin', (e) => {
  const cat = e.target.closest('[data-bp-mega-cat]');
  if (cat) activateMegaCat(cat);
});
function activateMegaCat(cat) {
  if (cat.classList.contains('is-active')) return;
  const mega = cat.closest('[data-bp-mega]');
  $$('[data-bp-mega-cat]', mega).forEach((c) => c.classList.toggle('is-active', c === cat));
  $$('[data-bp-mega-panel]', mega).forEach((p) => (p.hidden = p.dataset.bpMegaPanel !== cat.dataset.bpMegaCat));
}

/* ---------- Add several lines at once (quick order) ---------- */
async function addItems(items, sourceEl) {
  const root = window.Shopify?.routes?.root || '/';
  const deferred = CartLinesUpdateEvent.createPromise();
  (sourceEl || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then((r) => r.json());
    if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
    const cart = await refreshCartCount();
    deferred.resolve({ cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined, detail: { items: cart?.items, source: 'bp-quick-order', didError: false } });
    return true;
  } catch (err) {
    deferred.resolve({ detail: { didError: true } });
    toast(err.message);
    return false;
  }
}

function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    if (key === 'amount_no_decimals') return withCommas;
    if (key === 'amount_with_comma_separator') return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
    if (key === 'amount_no_decimals_with_comma_separator') return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${withCommas}.${dec}`;
  });
}

/* ---------- Product page ---------- */
class BpProduct extends HTMLElement {
  connectedCallback() {
    const json = $('[data-bp-product-json]', this);
    this.variants = json ? JSON.parse(json.textContent).variants : [];
    this.form = $('[data-bp-main-form]', this);
    this.track = $('[data-bp-gallery-track]', this);
    this.fieldsets = $$('[data-bp-option]', this);

    $$('[data-bp-thumb-index]', this).forEach((thumb) =>
      thumb.addEventListener('click', () => this.showMedia(Number(thumb.dataset.bpThumbIndex)))
    );
    // one image at a time: arrows step through the media
    $('[data-bp-gallery-prev]', this)?.addEventListener('click', () => this.showMedia(Math.max(0, (this.current || 0) - 1)));
    $('[data-bp-gallery-next]', this)?.addEventListener('click', () => this.showMedia(Math.min(this.track.children.length - 1, (this.current || 0) + 1)));
    // start on the image of the preselected variant (e.g. ?variant=...)
    const startId = Number($('[data-bp-variant-id]', this)?.value);
    const startMedia = this.variants.find((v) => v.id === startId)?.media;
    const startSlide = startMedia && $(`[data-media-id="${startMedia}"]`, this);
    if (startSlide) requestAnimationFrame(() => this.showMedia(Number(startSlide.dataset.index), true));
    this.track?.addEventListener('scroll', () => {
      clearTimeout(this._scrollT);
      this._scrollT = setTimeout(() => {
        this.markThumbs();
        this.alignSlide();
      }, 120);
    }, { passive: true });
    // keep the current image in place when the gallery width changes (images loading, rotation)
    if (this.track && 'ResizeObserver' in window) {
      let lastWidth = 0;
      new ResizeObserver(([entry]) => {
        const w = Math.round(entry.contentRect.width);
        if (lastWidth && w !== lastWidth) this.showMedia(this.current || 0, true);
        lastWidth = w;
      }).observe(this.track);
    }

    this.fieldsets.forEach((fs) =>
      fs.addEventListener('click', (e) => {
        const btn = e.target.closest('.bp-options__btn');
        if (!btn) return;
        $$('.bp-options__btn', fs).forEach((b) => b.classList.toggle('is-selected', b === btn));
        $('[data-bp-option-value]', fs).textContent = btn.dataset.label || btn.dataset.value;
        this.update();
      })
    );

    // rotating highlight boxes
    const infos = $$('.bp-pdp__info', this);
    if (infos.length > 1 && !reducedMotion) {
      let i = 0;
      setInterval(() => {
        infos[i].classList.remove('is-active');
        i = (i + 1) % infos.length;
        infos[i].classList.add('is-active');
      }, 4000);
    }

    // specifications: collapse long tables
    const specs = $('[data-bp-specs]', this);
    const rows = specs ? $$('tr', specs) : [];
    const toggle = specs && $('[data-bp-specs-toggle]', specs);
    if (toggle && rows.length > 11) {
      specs.classList.add('is-collapsed');
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const collapsed = specs.classList.toggle('is-collapsed');
        toggle.textContent = collapsed ? 'Read more' : 'Show less';
      });
    }

    // sticky bar
    const sticky = $('[data-bp-sticky]', this);
    const buy = $('.bp-pdp__buy', this);
    if (sticky && buy && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        $$('button, select', sticky).forEach((el) => (el.tabIndex = show ? 0 : -1));
      }).observe(buy);
      $('[data-bp-sticky-add]', sticky)?.addEventListener('click', () => this.form?.requestSubmit());
      // hide the bar while a section with its own add button (quick order, bought together) is on screen
      const blockers = new Set();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? blockers.add(en.target) : blockers.delete(en.target)));
        sticky.classList.toggle('is-suppressed', blockers.size > 0);
      });
      document.querySelectorAll('bp-quick-order, bp-fbt').forEach((el) => io.observe(el));
      $('[data-bp-sticky-variant]', sticky)?.addEventListener('change', (e) => this.selectVariantById(Number(e.target.value)));
    }
  }

  showMedia(index, instant = false) {
    const slide = this.track?.children[index];
    if (!slide) return;
    this.current = index;
    // 'instant', not 'auto': the track has scroll-behavior: smooth, and an animated jump can be cut short
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: instant ? 'instant' : 'smooth' });
    this.markThumbs(index);
  }

  // After scrolling stops, put the nearest image exactly in view (an interrupted scroll can stop between two)
  alignSlide() {
    const slide = this.track?.children[this.current];
    if (!slide) return;
    const left = slide.offsetLeft - this.track.offsetLeft;
    if (Math.abs(this.track.scrollLeft - left) > 2) this.track.scrollTo({ left, behavior: 'instant' });
  }

  markThumbs(forced) {
    if (!this.track) return;
    const slides = Array.from(this.track.children);
    const width = slides[0]?.getBoundingClientRect().width || 1;
    const first = forced ?? Math.round(this.track.scrollLeft / (width + 12));
    this.current = first;
    const perView = Math.max(1, Math.round(this.track.clientWidth / width));
    $$('[data-bp-thumb-index]', this).forEach((t) => {
      const i = Number(t.dataset.bpThumbIndex);
      t.classList.toggle('is-active', i >= first && i < first + perView);
    });
  }

  selectVariantById(id) {
    const variant = this.variants.find((v) => v.id === id);
    if (!variant) return;
    this.fieldsets.forEach((fs, i) => {
      $$('.bp-options__btn', fs).forEach((b) => b.classList.toggle('is-selected', b.dataset.value === variant.options[i]));
      const label = $('[data-bp-option-value]', fs);
      const btn = $$('.bp-options__btn', fs).find((x) => x.dataset.value === variant.options[i]);
      if (label) label.textContent = btn?.dataset.label || variant.options[i];
    });
    this.update();
  }

  update() {
    const selected = this.fieldsets.map((fs) => $('.bp-options__btn.is-selected', fs)?.dataset.value);
    markSoldOut(this.fieldsets, this.variants, selected);
    const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    const button = $('[data-bp-add-button]', this);
    const stock = $('[data-bp-stock]', this);
    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = S.unavailable;
      }
      return;
    }
    $('[data-bp-variant-id]', this).value = variant.id;
    const sku = $('[data-bp-sku]', this);
    if (sku) {
      sku.textContent = variant.sku || '';
      sku.parentElement.hidden = !variant.sku;
    }
    const priceHtml = `<span class="bp-price__current">${variant.price}</span>${variant.compare ? `<s class="bp-price__compare">${variant.compare}</s>` : ''}`;
    $$('[data-bp-price], [data-bp-sticky-price]', this).forEach((el) => {
      el.classList.toggle('bp-price--sale', Boolean(variant.compare));
      el.innerHTML = priceHtml;
    });
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? S.add : S.soldOut;
    }
    if (stock) {
      stock.classList.toggle('is-out', !variant.available);
      $('[data-bp-stock-text]', stock).textContent = variant.available ? S.inStock : S.soldOut;
    }
    const stickySelect = $('[data-bp-sticky-variant]', this);
    if (stickySelect) stickySelect.value = variant.id;
    if (variant.media) {
      const slide = $(`[data-media-id="${variant.media}"]`, this);
      if (slide) this.showMedia(Number(slide.dataset.index));
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.get('bp-product') || customElements.define('bp-product', BpProduct);

/* ---------- Quick order list ---------- */
class BpQuickOrder extends HTMLElement {
  connectedCallback() {
    this.format = $('[data-bp-money-format]', this)?.innerHTML.trim();
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-bp-qo-step]');
      if (step) {
        const input = $('[data-bp-qo-qty]', step.closest('[data-bp-qo-variant]'));
        input.value = Math.max(0, (Number(input.value) || 0) + Number(step.dataset.bpQoStep));
        this.recalc();
      }
      if (e.target.closest('[data-bp-qo-add]')) this.add();
    });
    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-bp-qo-qty]')) this.recalc();
    });
  }
  lines() {
    return $$('[data-bp-qo-variant]', this)
      .map((row) => ({ row, id: Number(row.dataset.bpQoVariant), price: Number(row.dataset.price), quantity: Math.max(0, Number($('[data-bp-qo-qty]', row).value) || 0) }));
  }
  recalc() {
    let count = 0;
    let total = 0;
    this.lines().forEach((l) => {
      count += l.quantity;
      total += l.quantity * l.price;
      $('[data-bp-qo-line]', l.row).textContent = formatMoney(l.quantity * l.price, this.format);
    });
    $('[data-bp-qo-count]', this).textContent = count;
    $('[data-bp-qo-grand]', this).textContent = formatMoney(total, this.format);
    const rowTotal = $('[data-bp-qo-grand-row]', this);
    if (rowTotal) rowTotal.textContent = formatMoney(total, this.format);
    $('[data-bp-qo-add]', this).disabled = count === 0;
  }
  async add() {
    const items = this.lines().filter((l) => l.quantity > 0).map((l) => ({ id: l.id, quantity: l.quantity }));
    if (!items.length) return;
    const btn = $('[data-bp-qo-add]', this);
    btn.classList.add('is-loading');
    const ok = await addItems(items, this);
    btn.classList.remove('is-loading');
    if (ok) {
      $$('[data-bp-qo-qty]', this).forEach((i) => (i.value = 0));
      this.recalc();
    }
  }
}
customElements.get('bp-quick-order') || customElements.define('bp-quick-order', BpQuickOrder);

/* ---------- /cart page ---------- */
class BpCartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const q = e.target.closest('[data-bp-line-qty]');
      if (q) {
        const line = q.closest('[data-bp-line]');
        const input = $('[data-bp-line-input]', line);
        return this.change(line.dataset.bpLine, (Number(input.value) || 0) + Number(q.dataset.bpLineQty));
      }
      const r = e.target.closest('[data-bp-line-remove]');
      if (r) return this.change(r.closest('[data-bp-line]').dataset.bpLine, 0);
      const rc = e.target.closest('[data-bp-discount-remove]');
      if (rc) {
        const codes = $$('[data-bp-discount-remove]', this).map((b) => b.dataset.bpDiscountRemove).filter((c) => c !== rc.dataset.bpDiscountRemove);
        return this.post('cart/update.js', { discount: codes.join(',') });
      }
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-bp-line-input]')) this.change(e.target.closest('[data-bp-line]').dataset.bpLine, Number(e.target.value) || 0);
      if (e.target.matches('[data-bp-cart-note]')) saveCustomerNote(e.target.value).catch(() => {});
    });
    this.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-bp-discount-form]')) return;
      e.preventDefault();
      const code = e.target.discount.value.trim();
      if (!code) return;
      const codes = [...new Set([...$$('[data-bp-discount-remove]', this).map((b) => b.dataset.bpDiscountRemove), code])];
      this.post('cart/update.js', { discount: codes.join(',') });
    });
    // stay in sync when the drawer or other add buttons change the cart
    document.addEventListener(StandardEvents?.cartLinesUpdate || 'cart:lines-update', (e) => {
      e.promise?.then(() => this.refresh()).catch(() => {});
    });
  }
  get root() { return window.Shopify?.routes?.root || '/'; }
  get sectionId() { return this.dataset.sectionId; }
  change(key, quantity) { return this.post('cart/change.js', { id: key, quantity: Math.max(0, quantity) }); }
  async post(url, body) {
    this.classList.add('is-updating');
    try {
      const res = await fetch(`${this.root}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: this.sectionId }) });
      const data = await res.json();
      if (data.status && data.status >= 400) throw new Error(data.description || data.message || 'Something went wrong');
      if (data.sections) this.render(data.sections[this.sectionId]);
      refreshCartCount();
    } catch (err) {
      toast(err.message);
    } finally {
      this.classList.remove('is-updating');
    }
  }
  async refresh() {
    const data = await (await fetch(`${this.root}cart?sections=${this.sectionId}`)).json();
    this.render(data[this.sectionId]);
  }
  render(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('bp-cart-page');
    if (fresh) this.innerHTML = fresh.innerHTML;
    updateShipBars(this);
  }
}
customElements.get('bp-cart-page') || customElements.define('bp-cart-page', BpCartPage);

/* ---------- Cart recommendations: open the variant picker ---------- */
document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-bp-rec-toggle]');
  if (!t) return;
  const pick = document.getElementById(t.dataset.bpRecToggle);
  if (!pick) return;
  pick.hidden = !pick.hidden;
  t.setAttribute('aria-expanded', String(!pick.hidden));
  if (!pick.hidden) pick.querySelector('select')?.focus();
});

/* ---------- Frequently bought together ---------- */
class BpFbt extends HTMLElement {
  connectedCallback() {
    this.format = $('[data-bp-money-format]', this)?.innerHTML.trim();
    this.addEventListener('change', () => this.recalc());
    $('[data-bp-fbt-add]', this)?.addEventListener('click', () => this.add());
    // keep the main item in sync with the variant picked on the page
    const mainInput = document.querySelector('[data-bp-main-form] [data-bp-variant-id]');
    mainInput && new MutationObserver(() => this.syncMain()).observe(mainInput, { attributes: true });
    document.addEventListener('click', (e) => { if (e.target.closest('.bp-options__btn')) setTimeout(() => this.syncMain(), 50); });
    this.recalc();
  }
  syncMain() {
    const input = document.querySelector('[data-bp-main-form] [data-bp-variant-id]');
    const main = $('[data-main]', this);
    if (!input || !main) return;
    main.dataset.variant = input.value;
    const priceText = document.querySelector('[data-bp-price] .bp-price__current')?.textContent || '';
    const cents = Math.round(parseFloat(priceText.replace(/[^0-9.,]/g, '').replace(/,(?=\d{3})/g, '').replace(',', '.')) * 100);
    if (cents) {
      main.dataset.price = cents;
      const label = main.closest('.bp-fbt__item')?.querySelector('.bp-fbt__price');
      if (label) label.textContent = priceText.trim();
    }
    this.recalc();
  }
  checked() { return $$('[data-bp-fbt]', this).filter((c) => c.checked); }
  recalc() {
    const boxes = $$('[data-bp-fbt]', this);
    boxes.forEach((c) => $(`[data-bp-fbt-img="${c.dataset.bpFbt}"]`, this)?.classList.toggle('is-off', !c.checked));
    const total = this.checked().reduce((s, c) => s + Number(c.dataset.price), 0);
    $('[data-bp-fbt-total]', this).textContent = formatMoney(total, this.format);
    const btn = $('[data-bp-fbt-add]', this);
    btn.disabled = !this.checked().length;
  }
  async add() {
    const items = this.checked().map((c) => ({ id: Number(c.dataset.variant), quantity: 1 }));
    if (!items.length) return;
    const btn = $('[data-bp-fbt-add]', this);
    btn.classList.add('is-loading');
    await addItems(items, this);
    btn.classList.remove('is-loading');
  }
}
customElements.get('bp-fbt') || customElements.define('bp-fbt', BpFbt);

/* ---------- Hotspots ---------- */
document.addEventListener('click', (e) => {
  const dot = e.target.closest('[data-bp-hotspot]');
  $$('.bp-hot__spot.is-open').forEach((spot) => {
    if (!dot || spot !== dot.parentElement) {
      spot.classList.remove('is-open');
      $('[data-bp-hotspot]', spot).setAttribute('aria-expanded', 'false');
    }
  });
  if (dot) {
    const open = dot.parentElement.classList.toggle('is-open');
    dot.setAttribute('aria-expanded', String(open));
  }
});

/* ---------- Free shipping bar in the customer's currency ---------- */
function updateShipBars(root = document) {
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  $$('[data-bp-ship]', root).forEach((bar) => {
    const { currency, shopCurrency } = bar.dataset;
    const total = Number(bar.dataset.total) || 0;
    // threshold is stored in the shop currency; convert it when the customer shops in another one
    const threshold = currency === shopCurrency ? Number(bar.dataset.threshold) : Math.round(Number(bar.dataset.threshold) * rate);
    const remaining = Math.max(0, threshold - total);
    const fill = $('[data-bp-ship-fill]', bar);
    if (fill) fill.style.width = `${threshold ? Math.min(100, (total / threshold) * 100) : 100}%`;
    const need = $('[data-bp-ship-need]', bar);
    const done = $('[data-bp-ship-done]', bar);
    if (need) need.hidden = remaining <= 0;
    if (done) done.hidden = remaining > 0;
    const amount = $('[data-bp-ship-remaining]', bar);
    if (amount && currency !== shopCurrency) {
      try {
        amount.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(remaining / 100);
      } catch (err) {
        amount.textContent = `${(remaining / 100).toFixed(2)} ${currency}`;
      }
    }
  });
}
updateShipBars();

/* ---------- Cart: new picks on every visit ----------
   Each product gets a random rank once per page load, so the drawer keeps the same picks while the
   customer edits the cart and shows different ones on the next visit. */
const bpRanks = new Map();
const bpRank = (id) => {
  if (!bpRanks.has(id)) bpRanks.set(id, Math.random());
  return bpRanks.get(id);
};
function rotateCart(root = document) {
  $$('[data-bp-rotate-list]', root).forEach((list) => {
    const show = Number(list.dataset.show) || 6;
    const items = $$('[data-bp-rotate]', list).sort((a, b) => bpRank(a.dataset.bpRotate) - bpRank(b.dataset.bpRotate));
    items.forEach((li, i) => {
      li.hidden = i >= show;
      list.appendChild(li);
    });
  });
  $$('[data-bp-bumps]', root).forEach((box) => {
    const slots = $$('[data-bp-rotate]', box).sort((a, b) => bpRank(a.dataset.bpRotate) - bpRank(b.dataset.bpRotate));
    const title = $('[data-bp-bumps-title]', box);
    const inCart = slots.filter((s) => 'inCart' in s.dataset);
    const others = slots.filter((s) => !('inCart' in s.dataset));
    const setMode = (slot, full) => {
      $('.bp-bumps__full', slot).hidden = !full;
      $('.bp-bumps__mini', slot).hidden = full;
    };
    if (!inCart.length) {
      // one random offer
      slots.forEach((s, i) => { s.hidden = i > 0; setMode(s, true); box.appendChild(s); });
      return;
    }
    // checked offers stay on top, the rest become "Complete your order" add-ons
    inCart.forEach((s) => { s.hidden = false; setMode(s, true); box.appendChild(s); });
    if (title) { title.hidden = !others.length; box.appendChild(title); }
    others.forEach((s, i) => { s.hidden = i >= 3; setMode(s, false); box.appendChild(s); });
  });
}
rotateCart();

/* ---------- Cart drawer ---------- */
const bpCart = (() => {
  const SECTION = 'bp-cart-drawer';
  const root = () => document.getElementById(`shopify-section-${SECTION}`);
  const routeRoot = window.Shopify?.routes?.root || '/';
  let activePane = null;
  let lastFocus = null;

  function syncCount(el) {
    const wrap = $('[data-bp-cart-count-value]', el);
    if (!wrap) return;
    const count = Number(wrap.dataset.bpCartCountValue) || 0;
    $$('[data-bp-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  function render(html) {
    const el = root();
    if (!el || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${SECTION}`) || doc.body;
    el.innerHTML = fresh.innerHTML;
    syncCount(el);
    updateShipBars(el);
    rotateCart(el);
    if (activePane) showPane(activePane);
    initShipping(el);
  }

  async function refresh() {
    const res = await fetch(`${routeRoot}?sections=${SECTION}`);
    const data = await res.json();
    render(data[SECTION]);
  }

  async function post(url, body) {
    const res = await fetch(`${routeRoot}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION }),
    });
    const data = await res.json();
    if (data.status && data.status >= 400) throw new Error(data.description || data.message || 'Something went wrong');
    if (data.sections) render(data.sections[SECTION]);
    if (Array.isArray(data.items)) syncPersonalizationNote(data).catch(() => {});
    return data;
  }

  function open() {
    const el = root();
    if (!el) return false;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    document.body.classList.add('bp-cart-lock');
    setTimeout(() => $('.bp-cart__close', el)?.focus({ preventScroll: true }), 50);
    return true;
  }

  function close() {
    const el = root();
    if (!el) return;
    el.classList.remove('is-open');
    document.body.classList.remove('bp-cart-lock');
    lastFocus?.focus?.({ preventScroll: true });
  }

  function showPane(name) {
    const el = root();
    activePane = name;
    $$('[data-bp-cart-pane]', el).forEach((p) => (p.hidden = p.dataset.bpCartPane !== name));
    $$('[data-bp-cart-tool]', el).forEach((t) => t.classList.toggle('is-active', t.dataset.bpCartTool === name));
  }

  async function changeLine(line, quantity) {
    line.classList.add('is-updating');
    try {
      await post('cart/change.js', { id: line.dataset.bpLine, quantity: Math.max(0, quantity) });
    } catch (err) {
      toast(err.message);
      line.classList.remove('is-updating');
    }
  }

  function currentCodes() {
    return $$('[data-bp-discount-remove]', root()).map((b) => b.dataset.bpDiscountRemove);
  }

  /* Estimate shipping: country/province selects built from all_country_option_tags */
  function initShipping(el) {
    const country = $('[data-bp-ship-country]', el);
    if (!country || country.dataset.ready) return;
    country.dataset.ready = '1';
    const def = country.dataset.default;
    if (def) {
      const match = Array.from(country.options).find((o) => o.value === def || o.textContent.trim() === def);
      if (match) country.value = match.value;
    }
    const fillProvinces = () => {
      const option = country.selectedOptions[0];
      const provinces = JSON.parse(option?.dataset.provinces || '[]');
      const wrap = $('[data-bp-ship-province-wrap]', el);
      const select = $('[data-bp-ship-province]', el);
      select.innerHTML = provinces.map(([value, label]) => `<option value="${value}">${label}</option>`).join('');
      wrap.hidden = provinces.length === 0;
    };
    country.addEventListener('change', fillProvinces);
    fillProvinces();
  }

  async function estimateShipping(form) {
    const out = $('[data-bp-ship-result]', form.parentElement);
    const params = new URLSearchParams();
    params.set('shipping_address[country]', form.country.value);
    if (!$('[data-bp-ship-province-wrap]', form).hidden) params.set('shipping_address[province]', form.province.value);
    params.set('shipping_address[zip]', form.zip.value);
    out.textContent = S.calculating;
    try {
      const prep = await fetch(`${routeRoot}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
      if (!prep.ok) {
        const err = await prep.json().catch(() => ({}));
        throw new Error(Object.values(err).flat().join(' ') || 'Could not calculate shipping for this address.');
      }
      let rates = null;
      for (let i = 0; i < 10 && !rates; i++) {
        const res = await fetch(`${routeRoot}cart/async_shipping_rates.json?${params}`);
        if (res.status === 200) rates = (await res.json()).shipping_rates;
        else await new Promise((r) => setTimeout(r, 600));
      }
      if (!rates || !rates.length) {
        out.textContent = S.noRates;
        return;
      }
      out.innerHTML = `<strong>${rates.length} shipping option${rates.length > 1 ? 's' : ''}:</strong><ul>${rates
        .map((r) => `<li>${r.name}: ${Number(r.price) === 0 ? 'Free' : `${r.price} ${r.currency}`}</li>`)
        .join('')}</ul>`;
    } catch (err) {
      out.textContent = err.message;
    }
  }

  async function shareCart() {
    const res = await fetch(`${routeRoot}cart.js`);
    const cart = await res.json();
    if (!cart.items.length) return;
    const url = `${window.location.origin}${routeRoot}cart/${cart.items.map((i) => `${i.variant_id}:${i.quantity}`).join(',')}`;
    try {
      if (navigator.share) await navigator.share({ title: document.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast(S.linkCopied);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') toast(S.shareFailed);
    }
  }

  document.addEventListener('click', (e) => {
    const el = root();
    if (!el) return;
    if (e.target.closest('[data-bp-cart-open]')) {
      e.preventDefault();
      refresh().catch(() => {});
      open();
      return;
    }
    if (!el.contains(e.target)) return;
    if (e.target.closest('[data-bp-cart-close]')) {
      if (e.target.closest('a[href]') && !e.target.closest('.bp-cart__overlay')) close();
      else {
        e.preventDefault();
        close();
      }
      return;
    }
    const qtyBtn = e.target.closest('[data-bp-line-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-bp-line]');
      const input = $('[data-bp-line-input]', line);
      changeLine(line, (Number(input.value) || 0) + Number(qtyBtn.dataset.bpLineQty));
      return;
    }
    const remove = e.target.closest('[data-bp-line-remove]');
    if (remove) {
      changeLine(remove.closest('[data-bp-line]'), 0);
      return;
    }
    const tool = e.target.closest('[data-bp-cart-tool]');
    if (tool) {
      showPane(activePane === tool.dataset.bpCartTool ? null : tool.dataset.bpCartTool);
      return;
    }
    if (e.target.closest('[data-bp-cart-share]')) {
      shareCart();
      return;
    }
    const removeCode = e.target.closest('[data-bp-discount-remove]');
    if (removeCode) {
      const codes = currentCodes().filter((c) => c !== removeCode.dataset.bpDiscountRemove);
      post('cart/update.js', { discount: codes.join(',') }).catch((err) => toast(err.message));
    }
  });

  document.addEventListener('change', (e) => {
    const el = root();
    if (!el || !el.contains(e.target)) return;
    const bump = e.target.closest('[data-bp-bump]');
    if (bump) {
      const box = bump.closest('.bp-bump');
      box?.classList.add('is-loading');
      const request = bump.checked
        ? post('cart/add.js', { items: [{ id: Number(bump.dataset.bpBump), quantity: 1 }] })
        : post('cart/change.js', { id: bump.dataset.line, quantity: 0 });
      request.then(() => refreshCartCount()).catch((err) => {
        toast(err.message);
        box?.classList.remove('is-loading');
      });
      return;
    }
    const input = e.target.closest('[data-bp-line-input]');
    if (input) changeLine(input.closest('[data-bp-line]'), Number(input.value) || 0);
    if (e.target.matches('[data-bp-cart-note]')) saveCustomerNote(e.target.value).catch(() => {});
  });

  document.addEventListener('submit', (e) => {
    const el = root();
    if (!el || !el.contains(e.target)) return;
    if (e.target.matches('[data-bp-discount-form]')) {
      e.preventDefault();
      const code = e.target.discount.value.trim();
      if (!code) return;
      const codes = [...new Set([...currentCodes(), code])];
      post('cart/update.js', { discount: codes.join(',') }).catch((err) => toast(err.message));
    } else if (e.target.matches('[data-bp-ship-form]')) {
      e.preventDefault();
      estimateShipping(e.target);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root()?.classList.contains('is-open')) close();
  });

  // Open + refresh after anything is added (our buttons and Horizon's product form)
  document.addEventListener(StandardEvents?.cartLinesUpdate || 'cart:lines-update', (e) => {
    e.promise
      ?.then(({ detail } = {}) => {
        if (detail?.didError) return;
        return refresh().then(() => {
          if (e.action === 'add' || e.detail?.action === 'add') open();
        });
      })
      .catch(() => {});
  });

  initShipping(document);
  return { open, close, refresh };
})();
window.bpCart = bpCart;

/* ---------- Side menu: sliding sub-panels ---------- */
document.addEventListener('click', (e) => {
  const next = e.target.closest('[data-bp-dnext]');
  if (next) {
    const panel = document.getElementById(next.dataset.bpDnext);
    if (!panel) return;
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    next.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-bp-dback]')?.focus({ preventScroll: true });
    return;
  }
  const back = e.target.closest('[data-bp-dback]');
  if (back) {
    const panel = back.closest('.bp-dpanel');
    closeDrawerPanel(panel);
  }
});
function closeDrawerPanel(panel) {
  if (!panel) return;
  panel.classList.remove('is-open');
  const opener = document.querySelector(`[data-bp-dnext="${panel.id}"]`);
  opener?.setAttribute('aria-expanded', 'false');
  setTimeout(() => {
    if (!panel.classList.contains('is-open')) panel.hidden = true;
  }, 350);
  opener?.focus({ preventScroll: true });
}
// Reset sub-panels whenever the side menu closes
$$('[data-bp-drawer]').forEach((drawer) => {
  drawer.addEventListener('toggle', () => {
    if (drawer.open) return;
    $$('.bp-dpanel', drawer).forEach((panel) => {
      panel.classList.remove('is-open');
      panel.hidden = true;
    });
    $$('[data-bp-dnext]', drawer).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  });
});

/* ---------- Collection page: filters, sorting, grid/list view ---------- */
$$('[data-bp-colform]').forEach((form) => {
  form.addEventListener('change', (e) => {
    if (e.target.matches('[data-bp-autosubmit]')) form.requestSubmit();
  });
  // Drop empty price inputs so they don't end up in the URL
  form.addEventListener('submit', () => {
    $$('input[type="number"]', form).forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
  });
  const toggle = $('[data-bp-filter-toggle]', form);
  const filters = $('[data-bp-filters]', form);
  toggle?.addEventListener('click', () => {
    if (!filters) return;
    const hidden = filters.classList.toggle('is-hidden');
    toggle.setAttribute('aria-expanded', String(!hidden));
  });
});

// Only one filter pill open at a time; close on outside click
document.addEventListener('click', (e) => {
  const openPill = e.target.closest('[data-bp-fpill]');
  $$('[data-bp-fpill][open]').forEach((pill) => {
    if (pill !== openPill) pill.removeAttribute('open');
  });
});

function setCollectionView(view) {
  $$('[data-bp-colgrid]').forEach((grid) => grid.classList.toggle('is-list', view === 'list'));
  $$('[data-bp-view]').forEach((btn) => btn.classList.toggle('is-active', btn.dataset.bpView === view));
}
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-bp-view]');
  if (!btn) return;
  setCollectionView(btn.dataset.bpView);
  try {
    localStorage.setItem('bp-collection-view', btn.dataset.bpView);
  } catch (err) {}
});
try {
  const savedView = localStorage.getItem('bp-collection-view');
  if (savedView) setCollectionView(savedView);
} catch (err) {}

/* ---------- Close drawer on Escape ---------- */
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') $$('[data-bp-drawer][open]').forEach((d) => d.removeAttribute('open'));
});

/* ---------- Deal of the day pop-up ---------- */
class BpDealPopup extends HTMLElement {
  connectedCallback() {
    this.storeKey = 'bp-deal-popup';
    if (this.seenThisVisit() || window.Shopify?.designMode) return;
    this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 2) * 1000);
    this.onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0) this.open(); };
    if (window.matchMedia('(pointer: fine)').matches) document.addEventListener('mouseout', this.onLeave);
    this.addEventListener('click', (e) => { if (e.target.closest('[data-bp-dpop-close]')) this.close(); });
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('mouseout', this.onLeave);
    document.removeEventListener('keydown', this.onKey);
  }
  seenThisVisit() {
    try { return sessionStorage.getItem(this.storeKey) === this.dataset.key; } catch (err) { return false; }
  }
  open() {
    if (!this.hidden || this.seenThisVisit()) return;
    // don't interrupt while the cart drawer is open
    if (document.querySelector('#shopify-section-bp-cart-drawer.is-open')) { this.timer = setTimeout(() => this.open(), 5000); return; }
    clearTimeout(this.timer);
    document.removeEventListener('mouseout', this.onLeave);
    try { sessionStorage.setItem(this.storeKey, this.dataset.key); } catch (err) { /* private mode */ }
    this.lastFocus = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.addEventListener('keydown', this.onKey);
    $('[data-bp-dpop-go]', this)?.focus({ preventScroll: true });
  }
  close() {
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKey);
    setTimeout(() => { this.hidden = true; }, 250);
    this.lastFocus?.focus?.({ preventScroll: true });
  }
}
customElements.get('bp-deal-popup') || customElements.define('bp-deal-popup', BpDealPopup);

/* ---------- Store amounts (free shipping) in the visitor's currency ---------- */
function convertAmounts(root = document) {
  const active = window.Shopify?.currency?.active;
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  $$('[data-bp-convert]', root).forEach((el) => {
    if (!active || active === el.dataset.shopCurrency) return;
    // round up to a whole unit: "over €43" is still true when the exact threshold is €42.61
    const value = Math.ceil((Number(el.dataset.bpConvert) / 100) * rate);
    try {
      el.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: active, maximumFractionDigits: 0 }).format(value);
    } catch (err) {
      el.textContent = `${value} ${active}`;
    }
  });
}
convertAmounts();

/* ---------- Local currency by location on the first visit ----------
   Asks Shopify which country the visitor is in (IP) and switches the store to it once.
   A country the visitor picks in the selector is always kept. */
(function geoCurrency() {
  const cfg = document.querySelector('[data-bp-geo]');
  if (!cfg || window.Shopify?.designMode || navigator.webdriver || /bot|crawl|spider|slurp/i.test(navigator.userAgent)) return;
  const KEY = 'bp-geo-currency';
  let done = null;
  try { done = localStorage.getItem(KEY); } catch (err) { return; }
  // a manual choice in the currency selector counts as done
  document.addEventListener('change', (e) => {
    if (e.target.matches('select[name="country_code"], select[name="locale_code"]')) { try { localStorage.setItem(KEY, 'manual'); } catch (err) { /* ignore */ } }
  });
  if (done) return;
  const current = cfg.dataset.country;
  const available = (cfg.dataset.available || '').split(',');
  fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true`)
    .then((r) => r.json())
    .then((data) => {
      const detected = data?.detected_values?.country?.handle;
      try { localStorage.setItem(KEY, detected || 'unknown'); } catch (err) { /* ignore */ }
      if (!detected) return;
      // language of the visitor's country, when the store offers it and the visitor is on the default language
      const LANG = { DE: 'de', AT: 'de', CH: 'de', LI: 'de', FR: 'fr', LU: 'fr', MC: 'fr', ES: 'es', IT: 'it', SM: 'it', NL: 'nl', BE: 'nl', PT: 'pt-PT' };
      const languages = (cfg.dataset.languages || '').split(',');
      const lang = LANG[detected];
      const switchLang = lang && languages.includes(lang) && cfg.dataset.language === cfg.dataset.defaultLanguage && lang !== cfg.dataset.language;
      const switchCountry = detected !== current && available.includes(detected);
      if (!switchLang && !switchCountry) return;
      const root = window.Shopify?.routes?.root || '/';
      const form = document.createElement('form');
      form.method = 'post';
      form.action = `${root}localization`;
      // return_to without the language prefix: Shopify adds the new one
      const path = '/' + window.location.pathname.slice(root.length);
      const fields = { form_type: 'localization', utf8: '✓', _method: 'put', return_to: path + window.location.search };
      if (switchCountry) fields.country_code = detected;
      if (switchLang) fields.locale_code = lang;
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    })
    .catch(() => {});
})();

/* ---------- Cookie banner: remember the visitor's answer ----------
   Shopify keeps consent in its own cookie, which can get lost (Safari limits, blocked cookies, a page change
   while the answer is still being sent, e.g. the first-visit country switch). The answer is also kept here
   for 12 months: the layout hides the banner early and this puts the same choice back through Shopify's API. */
(function consentMemory() {
  const KEY = 'bp-consent';
  const save = (c) => {
    try { localStorage.setItem(KEY, JSON.stringify({ analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, sale_of_data: !!c.sale_of_data, t: Date.now() })); } catch (err) { /* ignore */ }
  };
  // Banner buttons: saved on click, before Shopify's request finishes
  document.addEventListener('click', (e) => {
    if (e.target.closest?.('#shopify-pc__banner__btn-accept')) save({ analytics: true, marketing: true, preferences: true, sale_of_data: true });
    else if (e.target.closest?.('#shopify-pc__banner__btn-decline')) save({});
  }, true);
  // Any answer Shopify records (banner, preferences window, later changes)
  document.addEventListener('visitorConsentCollected', (e) => {
    const d = e.detail || {};
    save({ analytics: d.analyticsAllowed, marketing: d.marketingAllowed, preferences: d.preferencesAllowed, sale_of_data: d.saleOfDataAllowed });
  });
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (err) { return; }
  if (!saved || Date.now() - saved.t > 31536e6 || !window.Shopify?.loadFeatures) return;
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
    const cp = window.Shopify.customerPrivacy;
    if (err || !cp?.setTrackingConsent) return;
    const now = cp.currentVisitorConsent?.() || {};
    if (now.analytics || now.marketing || now.preferences) return; // Shopify still has the answer ('yes'/'no', '' when lost)
    const { t, ...choice } = saved;
    cp.setTrackingConsent(choice, () => {});
  });
})();

/* ---------- Product cards: color swatches preview that color's photo ---------- */
function swatchPreview(e, show) {
  const swatch = e.target.closest?.('[data-bp-swatch-img]');
  if (!swatch) return;
  const img = swatch.closest('.bp-card')?.querySelector('.bp-card__img:not(.bp-card__img--hover)');
  if (!img) return;
  if (show) {
    if (!img.dataset.bpOriginal) {
      img.dataset.bpOriginal = img.currentSrc || img.src;
      img.dataset.bpSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = swatch.dataset.bpSwatchImg;
    img.closest('.bp-card')?.classList.add('is-swatching');
  } else if (img.dataset.bpOriginal) {
    if (img.dataset.bpSrcset) img.setAttribute('srcset', img.dataset.bpSrcset);
    img.src = img.dataset.bpOriginal;
    img.closest('.bp-card')?.classList.remove('is-swatching');
  }
}
document.addEventListener('mouseover', (e) => swatchPreview(e, true));
document.addEventListener('mouseout', (e) => swatchPreview(e, false));
document.addEventListener('focusin', (e) => swatchPreview(e, true));
document.addEventListener('focusout', (e) => swatchPreview(e, false));
